import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Star, Clock, Zap, TrendingUp, X } from 'lucide-react';
import { Product } from '@/types';
import { formatPrice, truncateText } from '@/lib/utils';
import { calculateProductDiscount } from '@/lib/discount';
import { DiscountBadge } from './discount-badge';

interface RecommendedProductCardProps {
  product: Product;
  onClose: () => void;
}

export function RecommendedProductCard({ product, onClose }: RecommendedProductCardProps) {
  const [timeLeft, setTimeLeft] = useState(300); // 5 minutes
  const discountInfo = calculateProductDiscount(product, 1);

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          onClose();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [onClose]);

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const imageUrl = product.images && product.images.length > 0 ? product.images[0] : '';

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, x: 200 }}
        animate={{ opacity: 1, x: 0 }}
        exit={{ opacity: 0, x: 200 }}
        transition={{ type: 'spring', stiffness: 300, damping: 30 }}
        className="fixed bottom-8 left-1/2 transform -translate-x-1/2 z-50 w-[92%] max-w-sm sm:w-auto"
      >
        <div className="bg-white dark:bg-gray-900/95 backdrop-blur-sm rounded-xl shadow-2xl border border-gray-100/60 dark:border-gray-800/60 overflow-hidden relative px-3 py-2">
          <button
            onClick={onClose}
            aria-label="close"
            className="absolute top-2 right-2 z-20 p-1.5 rounded-full bg-gray-100/80 dark:bg-gray-700/80 hover:bg-gray-200/80 dark:hover:bg-gray-600/80 text-gray-600 dark:text-gray-300"
          >
            <motion.div whileHover={{ rotate: 90 }} transition={{ duration: 0.15 }}>
              <X className="h-3.5 w-3.5" />
            </motion.div>
          </button>

          <div className="flex items-center gap-3">
            <div className="flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-800">
              <Link to={`/product/${product.id}`} onClick={onClose} className="block w-full h-full">
                <img src={imageUrl} alt={product.produt_name} className="w-full h-full object-cover" />
              </Link>
            </div>

            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="inline-flex items-center gap-1 bg-gradient-to-r from-red-500 to-pink-500 text-white px-2 py-0.5 rounded-full text-xs font-semibold">
                    <TrendingUp className="h-3 w-3" />
                    HOT
                  </div>
                  <h4 className="ml-2 font-medium text-sm text-gray-900 dark:text-white line-clamp-2">{truncateText(product.produt_name, 40)}</h4>
                </div>

                <div className="flex items-center gap-2">
                  <div className="text-sm font-bold text-brand-primary dark:text-brand-light">{formatPrice(product.sellingprice)}</div>
                </div>
              </div>

              <div className="mt-2 flex items-center justify-between gap-2">
                <div className="flex items-center gap-1 text-xs text-gray-500">
                  <Clock className="h-3.5 w-3.5 text-red-500" />
                  <span className="font-semibold">{formatTime(timeLeft)}</span>
                </div>

                <div className="flex items-center gap-2">
                  <Link to={`/product/${product.id}`} onClick={onClose}>
                    <motion.button whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.96 }} className="bg-brand-primary text-white px-3 py-1.5 rounded-md text-xs font-medium">
                      View
                    </motion.button>
                  </Link>
                </div>
              </div>
            </div>
          </div>

          <motion.div animate={{ opacity: [0.25, 0.6, 0.25] }} transition={{ duration: 3, repeat: Infinity }} className="absolute inset-0 rounded-2xl bg-gradient-to-r from-red-500/10 to-pink-500/10 pointer-events-none" />
        </div>
      </motion.div>
    </AnimatePresence>
  );
}